import { useState } from 'react'
import { useDismissableMenu } from '@/hooks/useDismissableMenu'

/**
 * Neither the prototype's own `.topbar` markup nor its spec draws a user/account menu anywhere —
 * confirmed against `Prototype_v2.html` directly. But `a-changepw` ("a signed-in change") and
 * sign-out (`identitySignOut`) are both real, fully-built operations with no entry point anywhere
 * in the authenticated app at all, so this is the minimal real one rather than a redesign: the
 * same dropdown shape `TopBar.tsx`'s own `Switcher` establishes, with two static actions instead
 * of a row list.
 *
 * **Deliberately not in `src/components/`.** That directory is the catalogued component set
 * (`process/08-COMPONENT-CATALOGUE.md`, gated `COMPLETE` against the prototype's own markup) —
 * every root there must carry a `data-cmp` name from that catalogue, and this component has none
 * to carry: it renders nothing the prototype draws. Living here, in the shell layer that is its
 * only caller, is the honest home for a real addition with no prototype counterpart, rather than
 * inventing a fictitious catalogue entry to satisfy a lint scoped to catalogued components.
 */
export function AccountMenu({ onChangePassword, onSignOut }: { onChangePassword: () => void; onSignOut: () => void }) {
  const [open, setOpen] = useState(false)
  const { containerRef, triggerRef } = useDismissableMenu(open, () => setOpen(false))
  return (
    <div className="relative ml-auto" ref={containerRef}>
      <button ref={triggerRef} className="flex items-center gap-3 h-8 px-4 rounded-2 border border-bd2 text-body bg-panel" onClick={() => setOpen((v) => !v)}>
        Account
      </button>
      {open && (
        <div data-cmp="switcher-menu" className="absolute top-9 right-0 min-w-[200px] bg-panel border border-bd rounded-3 shadow-2 p-3 z-20">
          <button
            data-cmp="switcher-row"
            className="flex items-center w-full text-left px-3 py-3 rounded-2 hover:bg-row text-body"
            onClick={() => {
              setOpen(false)
              onChangePassword()
            }}
          >
            Change password
          </button>
          <button
            data-cmp="switcher-row"
            className="flex items-center w-full text-left px-3 py-3 rounded-2 hover:bg-row text-body"
            onClick={() => {
              setOpen(false)
              onSignOut()
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  )
}
